import { useMemo } from "react";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedVerificationServiceConfiguration } from "@idw-types/data/verification-service-configuration";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnCountry,
    AxnIdentityProvider,
    AxnRelyingParty,
    SimpleAxnWorkflow,
    AxnAttributeTypeFieldCustomization
} from "@idw-types/data/reference-data/axn";
import {
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    RpServiceType,
    SelfServiceVerificationAttribute
} from "@idw-types/data/reference-data/generic";
import { formatDateToString, sortDates } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { VerificationServicesTableCell } from "../verification-services-table-cell/verification-services-table-cell";
import styles from "./verification-services-table-container.less";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";

type VerificationServicesTableContainerProps = {
    verificationServiceConfigurations: Array<FormattedVerificationServiceConfiguration>;
    isPageHeight: boolean;
    axnCompanies: Array<AxnCompany>;
    axnCountries: Array<AxnCountry>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    messageTemplates: Array<FormattedMessageTemplate>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    selfServiceVerificationAttributes: Array<SelfServiceVerificationAttribute>;
    relyingParties: Array<AxnRelyingParty>;
    rpServiceTypes: Array<RpServiceType>;
    simpleAXNWorkflows: Array<SimpleAxnWorkflow>;
    simpleAXNAdminWorkflows: Array<SimpleAxnWorkflow>;
    attributeTypes: Array<AttributeType>;
    attributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
    showArchivedServices: boolean;
    tableState: TableState;
};

type VerificationServiceTableDataRow = FormattedVerificationServiceConfiguration & {
    relyingPartyName: string;
    workflowName: string;
    companyName: string;
    rpServiceTypeValue: string;
    displayName: string;
};

export const VerificationServicesTableContainer = (props: VerificationServicesTableContainerProps) => {
    const {
        verificationServiceConfigurations,
        isPageHeight,
        axnCompanies,
        axnCountries,
        axnIdentityProviders,
        messageTemplates,
        showArchivedServices,
        tableState,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        axnAvailablePropertyCategories,
        selfServiceVerificationAttributes,
        relyingParties,
        rpServiceTypes,
        simpleAXNWorkflows,
        simpleAXNAdminWorkflows,
        attributeClassifications,
        attributeLabels,
        attributeTypes,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps
    } = props;
    const dispatch = useDispatch();

    const verificationServiceTableData: Array<VerificationServiceTableDataRow> = useMemo(() => {
        return verificationServiceConfigurations.map(config => ({
            ...config,
            relyingPartyName: config.relyingParty?.name || resources.common.notFound,
            workflowName: config.simpleAXNWorkflow?.name || resources.common.notFound,
            companyName: config.company?.name || resources.common.notFound,
            rpServiceTypeValue: config.rpServiceType?.value || resources.common.unknown,
            displayName: config.status === EntityStatusTypes.INACTIVE
                ? `(${resources.common.archived}) ${config.serviceName}`
                : config.serviceName
        }));
    }, [verificationServiceConfigurations]);

    const columnHelper = createColumnHelper<VerificationServiceTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("displayName", {
            header: resources.tables.verificationServices.columns.serviceName.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.displayName, b.original.displayName),
            cell: (props) => (
                <VerificationServicesTableCell
                    value={props.getValue()}
                    verificationServiceConfiguration={props.row.original}
                    axnCompanies={axnCompanies}
                    axnCountries={axnCountries}
                    axnIdentityProviders={axnIdentityProviders}
                    axnAvailablePropertyCategories={axnAvailablePropertyCategories}
                    relyingParties={relyingParties}
                    rpServiceTypes={rpServiceTypes}
                    simpleAXNAdminWorkflows={simpleAXNAdminWorkflows}
                    simpleAXNWorkflows={simpleAXNWorkflows}
                    messageTemplates={messageTemplates}
                    axnAttributeTypeFields={axnAttributeTypeFields}
                    axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                    selfServiceVerificationAttributes={selfServiceVerificationAttributes}
                    attributeClassifications={attributeClassifications}
                    attributeLabels={attributeLabels}
                    attributeTypes={attributeTypes}
                    rpServiceAttributeProperties={rpServiceAttributeProperties}
                    rpServiceAttributePropertyMaps={rpServiceAttributePropertyMaps}
                />
            )
        }),
        columnHelper.accessor("apiKey", {
            header: resources.tables.verificationServices.columns.apiKey.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.apiKey, b.original.apiKey)
        }),
        columnHelper.accessor("relyingPartyName", {
            id: "relying-party-column",
            header: resources.tables.verificationServices.columns.relyingParty.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.relyingPartyName, b.original.relyingPartyName)
        }),
        columnHelper.accessor("workflowName", {
            id: "workflow-name-column",
            header: resources.tables.verificationServices.columns.workflow.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.workflowName, b.original.workflowName)
        }),
        columnHelper.accessor("companyName", {
            header: resources.tables.verificationServices.columns.organizationName.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.companyName, b.original.companyName)
        }),
        columnHelper.accessor("rpServiceTypeValue", {
            header: resources.tables.verificationServices.columns.serviceType.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("changeRequestStatus", {
            header: resources.tables.verificationServices.columns.crStatus.header,
            filterFn: "changeRequestStatusFilter",
            cell: (props) => {
                if (props.getValue()) {
                    const original = props.row.original;

                    const renderLastModifiedTooltip = (props: any) => (
                        <Tooltip id={`verification-service-${original.id}-last-modified-tooltip`} {...props}>
                            {resources.tables.verificationServices.tooltips.lastModified.label} {formatDateToString(original.lastModifiedDate, DISPLAY_DATE_WITH_TIME)}
                        </Tooltip>
                    );

                    return (
                        <div className={styles.changeRequestCell}>
                            <span>{props.getValue()}</span>
                            {props.row.original.lastModifiedDate && (
                                <div className={styles.lastModifiedTooltipContainer}>
                                    <OverlayTrigger placement="top" overlay={renderLastModifiedTooltip}>
                                        <span>
                                            <FontAwesomeIcon icon={faQuestionCircle} />
                                        </span>
                                    </OverlayTrigger>
                                </div>
                            )}
                        </div>
                    );
                } else {
                    return <span>{resources.common.notFound}</span>;
                }
            }
        }),
        columnHelper.accessor("lastDeploymentDate", {
            header: resources.tables.verificationServices.columns.lastDeployed.header,
            filterFn: "dateTimeFilter",
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            sortingFn: (a, b) => sortDates(a.original.lastDeploymentDate, b.original.lastDeploymentDate)
        })
    ]), [verificationServiceTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: verificationServiceTableData,
        state: tableState,
        filterFns: filterTypes,
        autoResetPageIndex: true,
        sortDescFirst: false,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.VERIFICATION_SERVICES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.VERIFICATION_SERVICES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.VERIFICATION_SERVICES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="verification-services"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"verification-services"}
                className={showArchivedServices ? styles.archivedVerificationServicesTable : styles.verificationServicesTable}
            />
            <PaginationContainer
                idPrefix={"verification-services"}
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};